import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { BlocksRenderer } from './BlockRenderer';
import { isValidLocale } from '../i18n';

/**
 * Wrapper that injects CMS blocks for a given page_key.
 * - When the page has `replaces_default_content=true` in `page_settings`, this
 *   wrapper is the SOLE content shown for the page (the parent component is
 *   responsible for hiding its hard-coded sections by calling usePageBlocks()).
 * - Otherwise blocks are simply appended below the original hard-coded content.
 */
export default function PageBlocks({ pageKey }) {
  const { lang } = useParams();
  const locale = lang && isValidLocale(lang) ? lang : 'es';
  const { blocks } = usePageBlocks(pageKey);
  if (!blocks || blocks.length === 0) return null;
  return <BlocksRenderer blocks={blocks} locale={locale} />;
}

/**
 * Hook to read both the blocks and the page_settings for a page.
 * Also tracks a single anonymous page view (fire-and-forget) so admins can
 * see traffic per page in the editor list.
 * Returns { blocks, replacesDefault, loading }.
 */
export function usePageBlocks(pageKey) {
  const { lang } = useParams();
  const locale = lang && isValidLocale(lang) ? lang : 'es';
  const [state, setState] = useState({ blocks: [], replacesDefault: false, loading: true });
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/pages/${pageKey}/blocks`).then(({ data }) => Array.isArray(data) ? data : []).catch(() => []),
      api.get(`/pages/${pageKey}/settings`).then(({ data }) => !!data?.replaces_default_content).catch(() => false),
    ]).then(([blocks, replacesDefault]) => {
      if (alive) setState({ blocks, replacesDefault, loading: false });
    });
    // Track a view (fire-and-forget). Errors silently ignored.
    api.post(`/pages/${pageKey}/track-view`, { locale, referrer: document.referrer || '' }).catch(() => {});
    return () => { alive = false; };
  }, [pageKey, locale]);
  return state;
}
